import { createSignal, Show, type JSX, type Component } from "solid-js";

export interface CollapsibleProps {
  /** Contenu de l'en-tête cliquable (texte, icône, résumé…). */
  header: JSX.Element;
  /** Ouvert par défaut (non contrôlé). Défaut : false. */
  defaultOpen?: boolean;
  /** Classe additionnelle sur le conteneur racine. */
  class?: string;
  /** Variante visuelle (thinking = grisé/italique, tool = encadré). */
  variant?: "default" | "thinking" | "tool" | "result";
  /** Notifie le parent du changement d'état (optionnel). */
  onToggle?: (open: boolean) => void;
  children?: JSX.Element;
}

/**
 * Bloc repliable clavier-accessible (Entrée/Espace), `aria-expanded`, chevron.
 * Réutilisé par la vue MESSAGES du panel CLI (thinking / tool_use / tool_result)
 * et disponible pour tout autre panel. État d'ouverture mémorisé localement.
 */
export const Collapsible: Component<CollapsibleProps> = (props) => {
  const [open, setOpen] = createSignal(props.defaultOpen ?? false);

  function toggle() {
    const next = !open();
    setOpen(next);
    props.onToggle?.(next);
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    }
  }

  return (
    <div
      class={`collapsible ${props.class || ""}`}
      data-variant={props.variant || "default"}
      data-open={open() || undefined}
    >
      <div
        class="collapsible-header"
        role="button"
        tabindex="0"
        aria-expanded={open()}
        onClick={toggle}
        onKeyDown={onKeyDown}
      >
        <span class="collapsible-chevron" aria-hidden="true">
          {open() ? "▾" : "▸"}
        </span>
        <span class="collapsible-header-body">{props.header}</span>
      </div>
      <Show when={open()}>
        <div class="collapsible-body">{props.children}</div>
      </Show>
    </div>
  );
};
